/**
 * Le formulaire de scan quand la messagerie choisie est Outlook / Hotmail :
 * le champ mot de passe laisse la place au bouton « Se connecter avec
 * Microsoft », et le jeton obtenu est glissé dans la demande de scan à la
 * place du mot de passe. Le jeton ne vit qu'en mémoire, dans cet onglet.
 */
import { FOURNISSEURS } from './fournisseurs.js';
import { seConnecterAvecMicrosoft, renouveler, jetonValable, reprendreRedirection, sansBoiteOutlook } from './microsoft.js';

let session = null;

const estMicrosoft = (cle) => Boolean(FOURNISSEURS[cle]?.microsoft);

const LOGO = '<svg viewBox="0 0 21 21" width="20" height="20" aria-hidden="true">'
  + '<rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/>'
  + '<rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>';

function element(balise, attributs = {}, enfants = []) {
  const e = Object.assign(document.createElement(balise), attributs);
  e.append(...enfants);
  return e;
}

export function brancherMicrosoft() {
  const select = document.getElementById('provider');
  const email = document.getElementById('email');
  const motDePasse = document.getElementById('password');
  if (!select || !email || !motDePasse) return;
  const champMotDePasse = motDePasse.closest('.field');

  const bouton = element('button', { type: 'button', className: 'bouton-microsoft', innerHTML: `${LOGO}<span>Se connecter avec Microsoft</span>` });
  const etat = element('p', { className: 'hint microsoft-etat' });
  const bloc = element('div', { className: 'field microsoft-field', hidden: true }, [
    element('label', { textContent: 'Connexion' }),
    bouton,
    etat
  ]);
  champMotDePasse.after(bloc);

  function afficherEtat() {
    etat.replaceChildren();
    bloc.classList.toggle('connecte', Boolean(session));
    if (!session) {
      etat.textContent = 'Pas de mot de passe à créer : tu te connectes sur la page de Microsoft, et Scanyt reçoit un accès temporaire à ta boîte.';
      bouton.querySelector('span').textContent = 'Se connecter avec Microsoft';
      return;
    }
    bouton.querySelector('span').textContent = 'Changer de compte Microsoft';
    if (sansBoiteOutlook(session)) {
      const domaine = session.email.split('@')[1];
      etat.append(
        element('strong', { className: 'alerte', textContent: '⚠ Pas de boîte Outlook' }),
        ` : ${session.email} est un compte Microsoft, mais ses emails sont chez ${domaine}. `
          + 'Choisis ta vraie messagerie dans « Fournisseur », ou connecte un compte @hotmail, @outlook ou @live.'
      );
      return;
    }
    etat.append(element('strong', { textContent: '✓ Connecté' }), ` : ${session.email}`);
  }

  // L'avertissement du formulaire parle de mot de passe d'application : pour
  // Microsoft, il n'y en a pas.
  const note = document.querySelector('#scan-form-card .inline-note');
  const noteHabituelle = note ? note.innerHTML : '';
  const noteMicrosoft = 'Avec Outlook, tu te connectes <strong>sur la page de Microsoft</strong> : Scanyt ne voit jamais '
    + 'ton mot de passe. L’accès ne sert que le temps du scan, n’est <strong>jamais enregistré</strong>, et ta boîte '
    + 'est lue directement depuis ton navigateur, en connexion chiffrée.';

  function appliquer() {
    const microsoft = estMicrosoft(select.value);
    if (note) note.innerHTML = microsoft ? noteMicrosoft : noteHabituelle;
    bloc.hidden = !microsoft;
    champMotDePasse.hidden = microsoft;
    motDePasse.required = !microsoft;
    if (microsoft && session) email.value = session.email;
  }

  bouton.addEventListener('click', async () => {
    const statut = document.getElementById('scan-status');
    bouton.disabled = true;
    bouton.querySelector('span').textContent = 'Connexion en cours…';
    try {
      session = await seConnecterAvecMicrosoft({ indice: email.value.trim() || undefined });
      if (session.email) email.value = session.email;
      if (statut) { statut.textContent = ''; statut.className = 'status'; }
    } catch (err) {
      if (statut) { statut.textContent = err.message; statut.className = 'status error'; }
    } finally {
      bouton.disabled = false;
      afficherEtat();
    }
  });

  // Sans connexion, pas de scan : on arrête l'envoi avant app.js. Un jeton
  // presque expiré est renouvelé, puis le formulaire repart tout seul.
  document.addEventListener('submit', async (evenement) => {
    if (evenement.target.id !== 'scan-form' || !estMicrosoft(select.value)) return;
    if (sansBoiteOutlook(session)) {
      evenement.preventDefault();
      evenement.stopImmediatePropagation();
      const statut = document.getElementById('scan-status');
      if (statut) {
        statut.textContent = 'Ce compte Microsoft n’a pas de boîte Outlook : ses emails sont ailleurs. Choisis ta messagerie dans « Fournisseur ».';
        statut.className = 'status error';
      }
      return;
    }
    if (jetonValable(session)) return;
    evenement.preventDefault();
    evenement.stopImmediatePropagation();
    const statut = document.getElementById('scan-status');
    if (session?.refreshToken) {
      try {
        session = await renouveler(session);
        evenement.target.requestSubmit();
        return;
      } catch { session = null; afficherEtat(); }
    }
    if (statut) { statut.textContent = 'Connecte-toi d’abord avec Microsoft.'; statut.className = 'status error'; }
    bouton.focus();
  }, true);

  select.addEventListener('change', appliquer);
  // La liste est remplie par app.js après un appel à l'API.
  new MutationObserver(appliquer).observe(select, { childList: true });
  afficherEtat();
  appliquer();

  // Retour d'une connexion faite en redirigeant l'onglet.
  reprendreRedirection().then((retrouvee) => {
    if (!retrouvee) return;
    session = retrouvee;
    const choisir = () => {
      if (![...select.options].some((o) => o.value === 'outlook')) return false;
      select.value = 'outlook';
      select.dispatchEvent(new Event('change'));
      afficherEtat();
      return true;
    };
    if (!choisir()) {
      const observateur = new MutationObserver(() => { if (choisir()) observateur.disconnect(); });
      observateur.observe(select, { childList: true });
    }
  }, (err) => {
    const statut = document.getElementById('scan-status');
    if (statut) { statut.textContent = err.message; statut.className = 'status error'; }
  });
}

/**
 * Corps de la demande de scan : pour Microsoft, le jeton remplace le mot de
 * passe, et l'adresse est celle du compte réellement connecté.
 */
export function preparerScan(corps) {
  const cle = document.getElementById('provider')?.value;
  if (!estMicrosoft(cle) || !session) return corps;
  const { password, ...reste } = corps;
  return { ...reste, email: session.email || corps.email, accessToken: session.accessToken };
}
